import {
  InMemoryCache,
  InMemoryCacheConfig,
  IntrospectionFragmentMatcher,
  NormalizedCacheObject
} from 'apollo-cache-inmemory'
import { createHttpLink } from 'apollo-link-http'
import { onError } from 'apollo-link-error'
import { ApolloClientOptions } from 'apollo-client'
import { ApolloLink, from } from 'apollo-link'
import { Context } from '@nuxt/types'
import { createPerformanceLink } from '../links/performanceLink'
import introspectionResult from '~/types/fragmentTypes'

/**
 * Fragment matcher for Apollo Client 2.x to properly handle union and interface types.
 * Transforms the possibleTypes format from codegen to the IntrospectionFragmentMatcher format.
 *
 * Note: Using 'UNION' for all abstract types is intentional. Apollo Client 2.x's
 * IntrospectionFragmentMatcher accepts both 'UNION' and 'INTERFACE', and the kind
 * is only used for filtering during parsing, not for actual fragment matching.
 */
const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: {
    __schema: {
      types: Object.entries(introspectionResult.possibleTypes).map(
        ([abstractType, concreteTypes]) => ({
          kind: 'UNION',
          name: abstractType,
          possibleTypes: concreteTypes.map((type) => ({ name: type }))
        })
      )
    }
  }
})

type AdditionalHeadersT = {
  'cf-connecting-ip'?: string | string[]
  'x-forwarded-for'?: string
  'x-forwarded-host'?: string
}

/**
 * Returns Apollo server's url depending upon the environment that Bifrost is running in¸
 *
 * @param {Context['$config']} config
 * @returns string
 */
export const getHttpUri = (config: Context['$config']): string => {
  if (process.server) {
    if (!config.apolloServerUri) {
      throw new Error('Apollo server URI is not configured')
    }
    return config.apolloServerUri as string
  }
  if (process.client) {
    if (!config.apolloClientUri) {
      throw new Error('Apollo client URI is not configured')
    }
    return config.apolloClientUri as string
  }
  throw new Error('Both process.server and process.client are false')
}

/**
 * Returns Apollo client configuration.
 *
 * @param  {Context} context
 * @returns ApolloClientOptions<NormalizedCacheObject> & { defaultHttpLink: boolean }
 */
export default ({
  req,
  $config,
  $sentry,
  error
}: Context): ApolloClientOptions<NormalizedCacheObject> & { defaultHttpLink: boolean } => {
  //? Global error handler for disabled user error.
  const errorLink = onError((gqlError) => {
    if (gqlError.graphQLErrors) {
      const errorObj = gqlError.graphQLErrors?.[0]
      if ($config.onPrem && errorObj && errorObj.message === 'User is disabled')
        error({ message: errorObj.message, statusCode: 403 })
    }
  })

  const httpLink = createHttpLink({
    uri: (operation) => {
      const baseUri = getHttpUri($config)
      // Append operation name as query param for Sentry trace visibility
      if (operation.operationName) {
        return `${baseUri}?op=${encodeURIComponent(operation.operationName)}`
      }
      return baseUri
    },
    credentials: 'include',
    fetchOptions: {
      mode: 'cors'
    }
  })

  const responseHeadersLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      const context = operation.getContext()
      const responseHeaders = context.response?.headers
      if (responseHeaders) {
        const headersObj: Record<string, string[]> = {}

        responseHeaders.forEach((value: string, key: string) => {
          const normalizedKey = key.toLowerCase()
          if (!headersObj[normalizedKey]) {
            headersObj[normalizedKey] = []
          }
          headersObj[normalizedKey].push(value)
        })

        if (context.responseHeadersRef && typeof context.responseHeadersRef === 'object') {
          context.responseHeadersRef = Object.assign(context.responseHeadersRef, headersObj)
        }
      }

      return response
    })
  })

  // Sentry GraphQL tracking link - adds breadcrumbs and context for GraphQL operations
  const sentryGraphQLLink = new ApolloLink((operation, forward) => {
    const operationName = operation.operationName || 'Unknown'
    const operationType =
      operation.query.definitions?.[0]?.kind === 'OperationDefinition'
        ? (operation.query.definitions[0] as any).operation
        : 'unknown'

    $sentry?.addBreadcrumb({
      category: 'graphql',
      message: operationName,
      level: 'info',
      data: {
        operationName,
        operationType,
        variables: operation.variables
      }
    })

    $sentry?.setContext('graphql', {
      operationName,
      operationType,
      variables: operation.variables
    })

    return forward(operation)
  })

  //? When a request is handled via SSR, nginx receives the internal Bifrost pod's IP as client IP.
  //? The following middleware handles forwarding the IP of the actual client.
  const forwardHeadersMiddleware = new ApolloLink((operation, forward) => {
    const additionalHeaders: AdditionalHeadersT = {}

    if (process.server) {
      const clientIp =
        (req.headers['x-forwarded-for'] as string)?.split(',').pop() || req.socket.remoteAddress

      additionalHeaders['x-forwarded-for'] = clientIp
      additionalHeaders['x-forwarded-host'] = req.headers.host

      if ('cf-connecting-ip' in req.headers) {
        additionalHeaders['cf-connecting-ip'] = req.headers['cf-connecting-ip']
      }
    } else if (process.client) {
      additionalHeaders['x-forwarded-host'] = window.location.hostname
    }

    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...additionalHeaders
      }
    }))

    return forward(operation)
  })

  // Performance link for SSR tracing (server-side only, handles its own feature gating)
  // Pass req so the link can retrieve the parent span stored by ssrPerformance middleware
  const performanceLink = process.server ? createPerformanceLink(req) : null

  return {
    defaultHttpLink: false,
    ssrMode: process.server,
    cache: new InMemoryCache({
      fragmentMatcher,
      typePolicies: {
        ADSOrganization: {
          keyFields: ['login']
        },
        BitbucketDataCenterProject: {
          keyFields: ['key']
        }
      }
    } as InMemoryCacheConfig),
    link: from([
      errorLink,
      sentryGraphQLLink,
      // Add performance link only on server-side
      ...(performanceLink ? [performanceLink] : []),
      forwardHeadersMiddleware,
      responseHeadersLink,
      httpLink
    ])
  }
}
